import React, { useState, useEffect } from "react";
import moment from "moment";
import axios from "../../../axios";
import * as url from "../../../utils/url";

const ThreadedConversationView = ({
  messages,
  isLoading,
  onClose,
  id,
  onNewMessage,
  user,
  adminAId,
}) => {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const currentUserId =
    user && user.profile && user.profile._id ? user.profile._id : null;
  const currentUserFirstName =
    user && user.profile && user.profile.firstName
      ? user.profile.firstName
      : "";
  const currentUserLastName =
    user && user.profile && user.profile.lastName
      ? user.profile.lastName
      : "";

  const isAdminA = (reply) => {
    if (!adminAId || !reply || !reply.senderId) return false;
    return String(reply.senderId) === String(adminAId);
  };

  const isMine = (reply) => {
    if (!reply) return false;
    // Logged-in user: match by senderId === currentUserId
    if (currentUserId && reply.senderId) {
      return String(reply.senderId) === String(currentUserId);
    }
    // Public / non-logged-in viewer: we don't have the viewer's _id,
    // but on the public route the only participants are the Trivvy recipient
    // (the viewer) and Admin A. So anything NOT from Admin A is "mine".
    if (!currentUserId) {
      return !isAdminA(reply);
    }
    return false;
  };

  const getSenderLabel = (reply) => {
    if (isMine(reply)) return "You";
    if (isAdminA(reply)) return "Admin";
    const first = reply.senderFirstName || "";
    const last = reply.senderLastName || "";
    const full = (first + " " + last).trim();
    return full || "User";
  };

  // Group messages by conversation thread (excluding parent since it's handled elsewhere)
  const groupMessagesByThread = (messages) => {
    // For threaded conversations, we only show replies
    if (!messages || messages.length === 0) return { replies: [] };
    
    const replies = messages;
    
    return { replies };
  };

  const { replies } = groupMessagesByThread(messages);

  if (isLoading) {
    return (
      <div className="threaded-conversation-loading">
        <div className="text-center py-4">
          <div style={{ fontSize: "14px", color: "#666" }}>
            Loading conversation...
          </div>
        </div>
      </div>
    );
  }

  if (replies.length === 0) {
    return (
      <div className="threaded-conversation-empty">
        <div className="text-center py-4 text-muted">
          No conversation history available.
        </div>
      </div>
    );
  }

  const handleSend = async function() {
    const trimmed = (message || "").trim();
    if (!trimmed || sending) return;

    setSending(true);
    setError(null);

    // Optimistic UI: append the current user's message locally
    const tempId = "local-" + Date.now();
    const newMsg = {
      id: tempId,
      senderType: "ADMIN",
      senderId: currentUserId,
      senderFirstName: currentUserFirstName,
      senderLastName: currentUserLastName,
      body: trimmed,
      createdAt: new Date().toISOString(),
      pending: true,
    };

    // Update replies with new message
    const updatedReplies = [...replies, newMsg];
    setMessage("");

    try {
      var replyUrl = (user && user.profile && (user.profile.tokens || user.profile._id))
        ? url.replyToTriviaResult
        : url.replyToTriviaResultPublic;
      const res = await axios.post(replyUrl, {
        triviaResultId: id,
        replyText: trimmed,
        channel: "SMS",
      });

      if (res && res.data && res.data.success) {
        const data = res.data;

        // If backend returns the saved message, replace the temp one
        if (data.message && data.message._id) {
          // Replace temp message with real one and update state
          const finalReplies = updatedReplies.map(function(m) {
            if (m.id === tempId) {
              return {
                id: data.message._id,
                senderType: data.message.senderType || "ADMIN",
                senderId:
                  data.message.senderId !== undefined
                    ? data.message.senderId
                    : currentUserId,
                senderFirstName:
                  data.message.senderFirstName !== undefined
                    ? data.message.senderFirstName
                    : currentUserFirstName,
                senderLastName:
                  data.message.senderLastName !== undefined
                    ? data.message.senderLastName
                    : currentUserLastName,
                body: data.message.body || trimmed,
                createdAt: data.message.createdAt || m.createdAt,
              };
            }
            return m;
          });
          
          // Update the replies state to include the new message
          // We need to trigger a re-render with the updated replies
          // Since we can't directly update state here, we'll update the messages prop
          // by calling a callback function passed from parent
          if (onNewMessage) {
            onNewMessage(finalReplies);
          }
        } else {
          // If no message returned, just add the temp message as final
          if (onNewMessage) {
            onNewMessage(updatedReplies);
          }
        }
        
        // Clear the input field
        setMessage("");
        setSending(false);
      } else {
        setError("Failed to send message.");
        setSending(false);
      }
    } catch (err) {
      console.error("Error sending reply", err);
      setError("Failed to send message.");
      setSending(false);
    }
  };

  return (
    <div className="threaded-conversation-inline">
      {/* Conversation Thread */}
      <div className="threaded-conversation-thread">
        {/* Reply Messages Only (Parent handled elsewhere) */}
        {replies.map((reply, index) => {
          const mine = isMine(reply);
          const bubbleBg = mine ? "#0084ff" : "#28a745";
          const bubbleColor = "white";
          const alignRight = mine;
          const label = getSenderLabel(reply);
          return (
          <div key={reply._id || index} className="threaded-reply-container">
            {/* Vertical connecting line */}
            <div className="threaded-vertical-line"></div>

            {/* Reply message bubble */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              {/* Sender label */}
              <div style={{
                display: "flex",
                justifyContent: alignRight ? "flex-end" : "flex-start",
                alignItems: "center",
                marginBottom: "2px",
                paddingLeft: "10px",
                paddingRight: "10px",
              }}>
                <span className="threaded-message-sender" style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#333",
                }}>
                  {label}
                </span>
              </div>

              {/* Message content in bubble - green for Admin A, blue for everyone else */}
              <div className={`threaded-message-bubble ${mine ? 'reply-message' : 'parent-message'}`} style={{
                background: bubbleBg,
                color: bubbleColor,
                padding: "10px",
                borderRadius: "8px",
                width: "100%",
                marginBottom: "5px",
                textAlign: alignRight ? "right" : "left",
              }}>
                {reply.body}
              </div>

              {/* Timestamp completely outside the bubble */}
              <div style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                paddingLeft: "10px",
                marginTop: "0px",
              }}>
                <span className="threaded-message-time" style={{
                  fontSize: "10px",
                  color: "#666",
                  fontWeight: "normal",
                }}>
                  {moment(reply.createdAt).format("MMM D, YYYY [at] h:mm A")}
                </span>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {/* Message Input Section */}
      <div style={{ marginTop: "15px", borderTop: "1px solid #e9ecef", paddingTop: "15px" }}>
        {error && <div style={{ color: "red", fontSize: "12px", marginBottom: "5px" }}>{error}</div>}
        
        <div style={{ display: "flex", gap: "10px" }}>
          <textarea
            className="form-control"
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your response..."
            style={{
              flex: 1,
              resize: "none",
              borderRadius: "8px",
              border: "1px solid #ddd",
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSend}
            disabled={sending || !message.trim()}
            style={{
              backgroundColor: "#007bff",
              border: "none",
              color: "white",
              padding: "10px 20px",
              borderRadius: "8px",
              cursor: sending ? "not-allowed" : "pointer",
              opacity: sending ? 0.6 : 1,
            }}
          >
            {sending ? "Sending..." : "Send"}
          </button>
        </div>
      </div>
      
      {/* Close button at the bottom */}
      <div className="threaded-conversation-actions" style={{ marginTop: "15px" }}>
        <button 
          className="threaded-conversation-close-btn"
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: '14px',
            cursor: 'pointer',
            color: 'inherit',
            padding: '5px 10px',
            borderRadius: '4px',
            fontWeight: 'bold',
            alignSelf: 'flex-end'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          Hide conversation
        </button>
      </div>
    </div>
  );
};

export default ThreadedConversationView;
